import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageIntro } from "@/components/site/PageIntro";
import { PublicationList } from "@/components/publications/PublicationItem";
import { Bibliography } from "@/components/publications/Bibliography";
import { SCHOLAR_PROFILES } from "@/data/scholarProfiles";
import { useLang } from "@/i18n";
import data from "@/data/publications.json";
import { JsonLd } from "@/components/site/JsonLd";
import { publicationsSchema } from "@/data/structuredData";

const verified = data.items.filter((i) => i.verified);
const featured = verified.filter((i) => i.featured);
const articles = verified.filter((i) => i.group === "article");
const others = verified.filter((i) => i.group === "other");

const Block = ({ id, title, lead, children, tone }) => (
  <section id={id} data-testid={`pubs-${id}`} className={tone ? "bg-[#EAE5D9]/60 border-y border-[#D3CEC4]/60" : ""}>
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-24 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <h2 className="font-serif font-medium text-3xl sm:text-4xl leading-tight text-[#1A2522]">{title}</h2>
        <p className="mt-4 text-[13px] text-[#495854] leading-relaxed">{lead}</p>
      </div>
      <div className="lg:col-span-8">{children}</div>
    </div>
  </section>
);

const Profiles = ({ t }) => (
  <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="scholar-profiles">
    <span className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#7A857F] mr-2">{t.profiles}</span>
    {SCHOLAR_PROFILES.map((p) => (
      <a key={p.id} href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`profile-${p.id}`}
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-[#1A2522]/30 hover:border-[#1A2522] text-[12px] tracking-[0.16em] uppercase font-semibold text-[#1A2522] transition-colors">
        {p.label} <ArrowUpRight className="w-3.5 h-3.5" />
      </a>
    ))}
  </div>
);

export default function Publications() {
  const { lang, t: all } = useLang();
  const t = all.publications;
  return (
    <>
      <Seo page="publications" />
      <JsonLd data={publicationsSchema(lang, verified)} />
      <PageIntro testId="publications-intro" overline={t.overline} title={t.title} lead={t.lead}>
        <Profiles t={t} />
      </PageIntro>
      {featured.length > 0 && (
        <Block id="selected" title={t.selected} lead={t.selectedLead} tone>
          <PublicationList items={featured} lang={lang} t={t} testId="selected-list" />
        </Block>
      )}
      <Block id="guidelines" title={t.guidelines} lead={t.guidelinesLead} tone={featured.length === 0}>
        <PublicationList items={articles.filter((i) => i.type === "guideline")} lang={lang} t={t} testId="guidelines-list" />
      </Block>
      <Block id="bibliography" title={t.bibliography} lead={t.bibliographyLead} tone={featured.length > 0}>
        <Bibliography items={articles} lang={lang} t={t} />
      </Block>
      <Block id="other-records" title={t.otherRecords} lead={t.otherRecordsLead} tone={featured.length === 0}>
        <PublicationList items={others} lang={lang} t={t} testId="other-records-list" />
      </Block>
    </>
  );
}
